import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { clientInfo } from 'configureBrand'
import getLanguageFromStorage from 'helpers/getLanguageFromStorage'
import { getCardType, userAccountInfo } from 'helpers/user'
import type { OriginalRootState } from 'redux/rootReducer'
import assetsAPI from 'services/assets'
import { DynamicContent } from 'typings/dynamicContent'

const sliceName = 'dynamicContentSlice'

interface DynamicContentState extends DynamicContent {
  status: 'initial' | 'pending' | 'fulfilled' | 'rejected'
}

export const initialState: DynamicContentState = {
  status: 'initial'
}

export const fetchDynamicContent = createAsyncThunk(
  `${sliceName}/fetchDynamicContent`,
  async () => {
    return assetsAPI.getDynamicContent(clientInfo.clientName)
  }
)

export const dynamicContentSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchDynamicContent.pending, state => {
        state.status = 'pending'
      })
      .addCase(fetchDynamicContent.fulfilled, (state, action) => {
        return { status: 'fulfilled', ...action.payload }
      })
      .addCase(fetchDynamicContent.rejected, state => {
        state.status = 'rejected'
      })
  }
})

export default dynamicContentSlice.reducer

/* Selector Functions */
const selectDynamicContent = (state: OriginalRootState) => state.dynamicContent

export const selectOfferPromo = createSelector(
  selectDynamicContent,
  ({ offerPromo }): { url?: string; text?: string } => {
    if (!offerPromo) {
      return {}
    }

    const { url, text } = offerPromo
    const lang = getLanguageFromStorage()

    // returns text for current language preference
    return { url, text: text[lang] }
  }
)

export const selectMarketingBanner = createSelector(
  selectDynamicContent,
  ({ marketingBanner }): { marketingUrl?: string; altText?: string } => {
    if (!marketingBanner) {
      return {}
    }

    const cardType = userAccountInfo.isProx ? 'prox' : getCardType()
    const marketingBannerByCardType = marketingBanner[cardType]

    if (!marketingBannerByCardType) {
      return {}
    }

    const { marketingUrl, text } = marketingBannerByCardType
    const lang = getLanguageFromStorage()

    return { marketingUrl, altText: text[lang] }
  }
)

export const selectCardReissueBanner = createSelector(
  selectDynamicContent,
  ({ cardReissue }) => {
    if (!cardReissue) {
      return {}
    }
    const lang = getLanguageFromStorage()
    const cardType = userAccountInfo.isProx ? 'prox' : 'brc'
    const {
      url,
      desktopText,
      mobileViewOneText,
      mobileViewTwoText,
      expiryDate
    } = cardReissue[cardType]

    return {
      url,
      desktopText: desktopText[lang],
      mobileViewOneText: mobileViewOneText[lang],
      mobileViewTwoText: mobileViewTwoText[lang],
      expiryDate
    }
  }
)
